import { createSlice } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { throttleToAnimationFrame } from '@utils/common';
import { makeSelectors } from '@utils/css';
import { createIndependentStore, useScrolledDown } from '@utils/react';

const scrollDirectionSelectors = makeSelectors({
  atTopOfPage: "top-of-page",
  scrollingDown: "scrolling-down",
  scrollingUp: "scrolling-up"
});
const scrollDirectionStore = createIndependentStore(createSlice({
  name: "hook/scroll-direction",
  initialState: {
    enabled: true,
    direction: "up"
  },
  reducers: {
    setState(state, action) {
      state.enabled = action.payload.enabled;
    },
    setDirection(state, action) {
      state.direction = action.payload;
      document.documentElement.classList.toggle(scrollDirectionSelectors.scrollingDown.className, action.payload === "down");
      document.documentElement.classList.toggle(scrollDirectionSelectors.scrollingUp.className, action.payload === "up");
    },
    forceDirection(state, action) {
      document.documentElement.classList.toggle(scrollDirectionSelectors.scrollingDown.className, action.payload === "down");
      document.documentElement.classList.toggle(scrollDirectionSelectors.scrollingUp.className, action.payload === "up");
    }
  }
}));

/**
 * detect scroll direction and apply/remove scrolledDown class name from body
 * element in response,
 */
function useScrollDirectionClass() {
  const enabled = scrollDirectionStore.useSelector(state => state.enabled);
  const direction = scrollDirectionStore.useSelector(state => state.direction);
  const scrolledDown = useScrolledDown();
  useTrackScrollDirection({
    enabled,
    minScrollOffsetPx: 100,
    scrollDownThresholdPx: 65,
    scrollUpThresholdPx: 65
  });
  useEffect(() => {
    document.documentElement.classList.toggle(scrollDirectionSelectors.atTopOfPage.className, !scrolledDown);
    if (!enabled) {
      return;
    }
    document.documentElement.classList.toggle(scrollDirectionSelectors.scrollingDown.className, direction === "down");
    document.documentElement.classList.toggle(scrollDirectionSelectors.scrollingUp.className, direction === "up");
  }, [direction, enabled, scrolledDown]);
  return {
    scrolledDown,
    direction
  };
}

/**
 * Provides the current user scroll direction (note that direction change is
 * only applied after the specified threshold has been reached).
 */
function useTrackScrollDirection(options = {
  enabled: true,
  minScrollOffsetPx: 65,
  scrollUpThresholdPx: 100,
  scrollDownThresholdPx: 5
}) {
  useEffect(() => {
    if (!options?.enabled) {
      return;
    }
    let lastDirectionChangeAt = window.pageYOffset;
    const handleScroll = throttleToAnimationFrame(() => {
      const currScrollY = window.pageYOffset;
      if (currScrollY <= options.minScrollOffsetPx) {
        // if the user hasn't scrolled the defined minimum offset down on the
        // page, then enforce that the scroll direction is considered to be "up"
        scrollDirectionStore.dispatch(scrollDirectionStore.actions.setDirection("up"));
      } else {
        // ... otherwise determine if user has scrolled beyond the defined
        // threshold, and update scroll direction in that case
        if (window.pageYOffset < lastDirectionChangeAt && lastDirectionChangeAt - window.pageYOffset >= options.scrollUpThresholdPx) {
          scrollDirectionStore.dispatch(scrollDirectionStore.actions.setDirection("up"));
          lastDirectionChangeAt = window.pageYOffset;
        } else if (window.pageYOffset > lastDirectionChangeAt && window.pageYOffset - lastDirectionChangeAt >= options.scrollDownThresholdPx) {
          scrollDirectionStore.dispatch(scrollDirectionStore.actions.setDirection("down"));
          lastDirectionChangeAt = window.pageYOffset;
        }
      }
    });
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [options?.enabled, options.minScrollOffsetPx, options.scrollDownThresholdPx, options.scrollUpThresholdPx]);
}

export { scrollDirectionSelectors, scrollDirectionStore, useScrollDirectionClass };
//# sourceMappingURL=scroll-direction.js.map
